import * as PIXI from "pixi.js";
import {
  type MapConfig,
  DEFAULT_CONFIG,
  zoomToScale,
  scaleToZoom,
} from "./config.js";
import { Camera } from "./camera.js";
import { TileManager } from "./tile-manager.js";
import { InputHandler } from "./input.js";
import { buildIconSvg } from "./icons.js";
import type { HighlightMarkerOptions } from "./layer-packs.js";
import { LayerPanel, type LayerPanelItem } from "./layer-panel.js";
import { Minimap } from "./minimap.js";
import { MapControls } from "./map-controls.js";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ViewState {
  x: number;
  y: number;
  zoom: number;
  bounds: { x1: number; y1: number; x2: number; y2: number };
}

export interface LayerInfo {
  id: string;
  visible: boolean;
  opacity: number;
  order: number;
  category?: string;
}

export interface LayerDef {
  id: string;
  label?: string;
  visible?: boolean;
  opacity?: number;
  hidden?: boolean;
  minZoom?: LayerMinZoom;
}

export interface CategoryDef {
  type: "category";
  label: string;
  layers: LayerDef[];
}

export type CreateLayersItem = LayerDef | CategoryDef;

export type MarkerShape = "circle" | "square" | "hexagon" | "shield";

export type LayerMinZoom = "default" | "low" | number;

export interface RichTooltip {
  title: string;
  subtitle?: string;
  badge?: { text: string; color: number };
}

export interface MarkerData {
  id: string;
  layer: string;
  x: number;
  y: number;
  icon?: string;
  shape?: MarkerShape;
  text?: string;
  tooltip?: string;
  richTooltip?: RichTooltip;
  color?: number;
  size?: number;
  data?: Record<string, unknown>;
}

export interface OverlayData {
  id: string;
  layer: string;
  _type: "polyline" | "polygon";
  points: Array<{ x: number; y: number }>;
  color?: number;
  fill?: number;
  width?: number;
  alpha?: number;
}

export interface HeatmapPoint {
  x: number;
  y: number;
  intensity: number;
}

export interface HeatmapData {
  id: string;
  layer: string;
  points: HeatmapPoint[];
  color?: number;
  radius?: number;
}

type EventCallback = (payload: Record<string, unknown>) => void;

// ── Layer entry ──────────────────────────────────────────────────────────────

interface LayerEntry {
  container: PIXI.Container;
  visible: boolean;
  opacity: number;
  /** Whether this is a built-in layer (tiles) that can't be removed. */
  builtin: boolean;
  /** Render order (lower = further back). */
  order: number;
  /** Display label for the layer panel. Falls back to the layer ID. */
  label?: string;
  /** Category label for panel grouping. */
  category?: string;
  /** If true, layer works but doesn't appear in the layer panel. */
  hidden: boolean;
  /** Resolved minimum zoom level (0 = always visible). */
  minZoom: number;
}

// ── Marker entry ─────────────────────────────────────────────────────────────

interface MarkerEntry {
  container: PIXI.Container;
  gfx: PIXI.Graphics;
  iconSprite: PIXI.Sprite | null;
  textDisplay: PIXI.Text | null;
  tooltipContainer: PIXI.Container | null;
  /** Original marker-local tooltip offset (for restoring after overlay reparent). */
  tooltipLocalX: number;
  tooltipLocalY: number;
  data: MarkerData;
  /** Highlight pulse multiplier (1.0 = no pulse). Applied on top of counter-scale. */
  pulseScale: number;
  /** Cancel function for any active pulse animation. */
  cancelPulse: (() => void) | null;
}

// ── Overlay entry ────────────────────────────────────────────────────────────

interface OverlayEntry {
  gfx: PIXI.Graphics;
  data: OverlayData;
}

// ── Heatmap entry ────────────────────────────────────────────────────────────

interface HeatmapEntry {
  sprite: PIXI.Sprite;
  renderTexture: PIXI.RenderTexture;
  data: HeatmapData;
}

// ── Gradient texture (shared by all heatmaps) ───────────────────────────────

let _gradientTexture: PIXI.Texture | null = null;

function getGradientTexture(): PIXI.Texture {
  if (_gradientTexture) return _gradientTexture;
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const half = size / 2;
  const grad = ctx.createRadialGradient(half, half, 0, half, half, half);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  _gradientTexture = PIXI.Texture.from(canvas);
  return _gradientTexture;
}

// ── Icon texture cache ───────────────────────────────────────────────────────

const iconTextureCache = new Map<string, PIXI.Texture>();

function getIconTexture(iconName: string): PIXI.Texture | null {
  const key = iconName;
  if (iconTextureCache.has(key)) return iconTextureCache.get(key)!;

  const svg = buildIconSvg(iconName, 48, "white");
  if (!svg) return null;

  const dataUri = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  const texture = PIXI.Texture.from(dataUri);
  iconTextureCache.set(key, texture);
  return texture;
}

// ── Shape drawing ────────────────────────────────────────────────────────────

function drawShape(
  gfx: PIXI.Graphics,
  shape: MarkerShape,
  size: number,
): void {
  switch (shape) {
    case "circle":
      gfx.drawCircle(0, 0, size);
      break;
    case "square":
      gfx.drawRect(-size, -size, size * 2, size * 2);
      break;
    case "hexagon": {
      const pts: number[] = [];
      for (let i = 0; i < 6; i++) {
        // Flat-top hexagon: start at -30°
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        pts.push(Math.cos(angle) * size, Math.sin(angle) * size);
      }
      gfx.drawPolygon(pts);
      break;
    }
    case "shield": {
      // Shield: wide top, pointed bottom
      const r = size;
      const pts = [
        -r,
        -r * 0.85,
        r,
        -r * 0.85,
        r,
        r * 0.15,
        0,
        r,
        -r,
        r * 0.15,
      ];
      gfx.drawPolygon(pts);
      break;
    }
  }
}

// ── Layer minZoom helpers ────────────────────────────────────────────────────

const LOW_MIN_ZOOM = 4;

/** Resolve a LayerMinZoom value to a numeric zoom level. */
function resolveMinZoom(v: LayerMinZoom | undefined): number {
  if (v === "low") return LOW_MIN_ZOOM;
  if (typeof v === "number") return v;
  return 0; // "default" or undefined → always visible
}

// ── Padding helpers ──────────────────────────────────────────────────────────

interface ResolvedPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

type Padding =
  | number
  | { top?: number; right?: number; bottom?: number; left?: number };

const ZERO_PADDING: ResolvedPadding = { top: 0, right: 0, bottom: 0, left: 0 };

function normalizePadding(p: Padding | undefined): ResolvedPadding {
  if (p == null) return ZERO_PADDING;
  if (typeof p === "number") {
    return p === 0 ? ZERO_PADDING : { top: p, right: p, bottom: p, left: p };
  }
  const top = p.top ?? 0;
  const right = p.right ?? 0;
  const bottom = p.bottom ?? 0;
  const left = p.left ?? 0;
  return top === 0 && right === 0 && bottom === 0 && left === 0
    ? ZERO_PADDING
    : { top, right, bottom, left };
}

// ── MapEngine ────────────────────────────────────────────────────────────────

export class MapEngine {
  private app: PIXI.Application;
  private world: PIXI.Container;
  private camera: Camera;
  private tileManager: TileManager;
  private input: InputHandler;
  private config: MapConfig;

  private layers = new Map<string, LayerEntry>();
  private markers = new Map<string, MarkerEntry>();
  private overlays = new Map<string, OverlayEntry>();
  private heatmaps = new Map<string, HeatmapEntry>();

  private readyCallbacks: Array<() => void> = [];
  private isReady = false;

  private eventCallbacks = new Map<string, Set<EventCallback>>();

  // Theme tracking for tooltip styling
  private currentTheme: "dark" | "light" = "dark";

  // Built-in tile container
  private tileContainer: PIXI.Container;

  // Layer ordering & panel
  private nextOrder = 0;
  private layerPanel: LayerPanel;

  // Minimap & controls
  private minimap: Minimap;
  private controls: MapControls;

  // Screen dimensions
  private screenW = 0;
  private screenH = 0;

  // Top-level container for visible tooltips so they render above all layers
  private tooltipOverlay: PIXI.Container;

  // Marker update cache — avoid re-processing every frame
  private lastMarkerScale = -1;
  private lastMarkerCamX = NaN;
  private lastMarkerCamY = NaN;
  private layerVisibilityCache = new Map<string, boolean>();
  private activePulses = 0;

  // Track whether the initial fitBounds used a valid viewport size.
  // When embedded in an iframe that hasn't laid out yet, the initial
  // size can be 0 or tiny, producing a wrong camera position.
  private initScreenW = 0;
  private initScreenH = 0;
  private needsInitRefit = true;

  // Global viewport padding applied to all navigation methods
  private viewportPadding: ResolvedPadding = ZERO_PADDING;

  // Bound handlers for focus-loss tooltip dismissal (stored for cleanup)
  private _onBlur: () => void;
  private _onVisibilityChange: () => void;

  constructor(
    container: HTMLElement,
    configOverrides: Partial<MapConfig> = {},
  ) {
    this.config = { ...DEFAULT_CONFIG, ...configOverrides };

    // Create Pixi app
    this.app = new PIXI.Application({
      resizeTo: container,
      backgroundColor: this.config.backgroundColor,
      antialias: false,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    container.appendChild(this.app.view as unknown as HTMLElement);

    this.screenW = this.app.screen.width;
    this.screenH = this.app.screen.height;

    // World container (scaled/positioned by camera)
    this.world = new PIXI.Container();
    this.app.stage.addChild(this.world);

    // Camera
    this.camera = new Camera(this.config);
    this.camera.fitToView(this.screenW, this.screenH);

    // Built-in tile container
    this.tileContainer = new PIXI.Container();
    this.world.addChild(this.tileContainer);

    // Register tiles as a built-in layer
    this.layers.set("tiles", {
      container: this.tileContainer,
      visible: true,
      opacity: 1.0,
      builtin: true,
      order: -1,
      hidden: true,
      minZoom: 0,
    });

    // Tooltip overlay – a stage-level container (screen space) so tooltips
    // render above every layer regardless of which layer the marker is on.
    this.tooltipOverlay = new PIXI.Container();
    this.app.stage.addChild(this.tooltipOverlay);

    // Tile manager
    this.tileManager = new TileManager(this.config, this.tileContainer);

    // Input handler
    const view = this.app.view as unknown as HTMLCanvasElement;
    view.style.cursor = "grab";
    this.input = new InputHandler(this.camera, view);
    this.input.onClick = (sx, sy) => {
      const { wx, wy } = this.camera.screenToWorld(sx, sy);
      this.emitEvent("click", { wx, wy });
    };

    // Layer panel
    this.layerPanel = new LayerPanel(container, {
      onToggle: (id, visible) => this.setLayerVisibility(id, visible),
      onCategoryToggle: (category, visible) => {
        for (const [, layer] of this.layers) {
          if (layer.category === category) {
            layer.visible = visible;
            layer.container.visible = visible;
          }
        }
        this.rebuildLayerPanel();
      },
    });

    // Minimap
    this.minimap = new Minimap(container, this.config.worldSize);

    // Controls
    this.controls = new MapControls(container, this.camera, () => {
      const rect = (this.app.view as unknown as HTMLCanvasElement).getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    });

    // Dismiss all tooltips when the window/iframe loses focus so they
    // don't get stuck open (pointerout never fires on focus loss).
    this._onBlur = () => this.dismissAllTooltips();
    this._onVisibilityChange = () => {
      if (document.hidden) this.dismissAllTooltips();
    };
    window.addEventListener("blur", this._onBlur);
    document.addEventListener("visibilitychange", this._onVisibilityChange);

    // Main loop
    let lastZoom = -1;
    this.app.ticker.add(() => {
      this.screenW = this.app.screen.width;
      this.screenH = this.app.screen.height;

      // When embedded in an iframe, the initial container size may be 0 or
      // very small before the layout is ready. Once we get a real size that
      // differs significantly from what was used at init, re-fit the world
      // and snap the camera so the user sees the full map.
      if (this.needsInitRefit && this.screenW > 0 && this.screenH > 0) {
        const sizeChanged =
          Math.abs(this.screenW - this.initScreenW) > 50 ||
          Math.abs(this.screenH - this.initScreenH) > 50;
        if (sizeChanged || this.initScreenW === 0 || this.initScreenH === 0) {
          this.fitBounds(0, 0, this.config.worldSize, this.config.worldSize, {
            padding: 0,
          });
          this.camera.scale = this.camera.targetScale;
          this.camera.x = this.camera.targetX;
          this.camera.y = this.camera.targetY;
        }
        // Only refit once — after we have a real size, stop checking.
        if (this.screenW > 50 && this.screenH > 50) {
          this.needsInitRefit = false;
        }
      }

      this.camera.update(this.screenW, this.screenH);

      this.world.scale.set(this.camera.scale);
      this.world.x = this.camera.x;
      this.world.y = this.camera.y;

      this.tileManager.update(this.camera, this.screenW, this.screenH);
      this.minimap.update(this.camera, this.screenW, this.screenH);

      // Counter-scale markers for fixed screen size + minZoom visibility
      this.updateMarkers();

      // Detect zoom/move end
      const currentZoom = Math.round(
        scaleToZoom(this.camera.scale, this.config) * 100,
      );
      if (currentZoom !== lastZoom) {
        lastZoom = currentZoom;
        this.emitEvent("zoomend", {
          zoom: scaleToZoom(this.camera.scale, this.config),
        });
      }
    });

    // Resize handler
    window.addEventListener("resize", () => {
      this.screenW = this.app.screen.width;
      this.screenH = this.app.screen.height;
    });

    // Default view: fit the full world
    this.fitBounds(0, 0, this.config.worldSize, this.config.worldSize, {
      padding: 0,
    });
    // Snap instantly (no easing from origin)
    this.camera.scale = this.camera.targetScale;
    this.camera.x = this.camera.targetX;
    this.camera.y = this.camera.targetY;

    // Record size used for init — if the container hasn't laid out yet
    // (common in iframe embeds), we'll re-fit once it reaches real size.
    this.initScreenW = this.screenW;
    this.initScreenH = this.screenH;

    // Signal ready
    this.markReady();
  }

  // ── Ready ──────────────────────────────────────────────────────────────

  whenReady(fn: () => void): void {
    if (this.isReady) {
      fn();
      return;
    }
    this.readyCallbacks.push(fn);
  }

  private markReady(): void {
    this.isReady = true;
    for (const fn of this.readyCallbacks) fn();
    this.readyCallbacks = [];
  }

  // ── Events ─────────────────────────────────────────────────────────────

  emitEvent(name: string, payload: Record<string, unknown>): void {
    const handlers = this.eventCallbacks.get(name);
    if (handlers) {
      for (const fn of handlers) {
        try {
          fn(payload);
        } catch (e) {
          console.error(`[MapEngine] event "${name}" handler error:`, e);
        }
      }
    }
  }

  onEvent(name: string, fn: EventCallback): void {
    if (!this.eventCallbacks.has(name)) {
      this.eventCallbacks.set(name, new Set());
    }
    this.eventCallbacks.get(name)!.add(fn);
  }

  offEvent(name: string, fn: EventCallback): void {
    this.eventCallbacks.get(name)?.delete(fn);
  }

  // ── Viewport Padding ───────────────────────────────────────────────────

  setViewportPadding(padding: Padding): void {
    this.viewportPadding = normalizePadding(padding);
  }

  /** Resolve effective padding: per-call override > global default. */
  private effectivePadding(perCall: Padding | undefined): ResolvedPadding {
    return perCall != null ? normalizePadding(perCall) : this.viewportPadding;
  }

  /** Screen-space center of the logical viewport after padding is applied. */
  private paddedCenter(pad: ResolvedPadding): { cx: number; cy: number } {
    return {
      cx: (pad.left + this.screenW - pad.right) / 2,
      cy: (pad.top + this.screenH - pad.bottom) / 2,
    };
  }

  // ── Navigation ─────────────────────────────────────────────────────────

  flyTo(
    x: number,
    y: number,
    opts: { zoom?: number; duration?: number; padding?: Padding } = {},
  ): void {
    const cam = this.camera;
    const pad = this.effectivePadding(opts.padding);
    const { cx, cy } = this.paddedCenter(pad);
    const targetScale =
      opts.zoom != null
        ? zoomToScale(opts.zoom, this.config)
        : cam.targetScale;

    cam.zoomAnchorWX = x;
    cam.zoomAnchorWY = y;
    cam.zoomAnchorSX = cx;
    cam.zoomAnchorSY = cy;
    cam.isZooming = true;
    cam.targetScale = targetScale;
    cam.targetX = cx - x * targetScale;
    cam.targetY = cy - y * targetScale;
    cam.vx = 0;
    cam.vy = 0;
  }

  panTo(x: number, y: number): void {
    const cam = this.camera;
    const pad = this.viewportPadding;
    const { cx, cy } = this.paddedCenter(pad);
    cam.zoomAnchorWX = x;
    cam.zoomAnchorWY = y;
    cam.zoomAnchorSX = cx;
    cam.zoomAnchorSY = cy;
    cam.isZooming = true;
    cam.targetX = cx - x * cam.targetScale;
    cam.targetY = cy - y * cam.targetScale;
    cam.vx = 0;
    cam.vy = 0;
  }

  setCenter(x: number, y: number, zoom?: number): void {
    const cam = this.camera;
    if (zoom != null) {
      cam.targetScale = zoomToScale(zoom, this.config);
      cam.scale = cam.targetScale;
    }
    cam.targetX = this.screenW / 2 - x * cam.targetScale;
    cam.targetY = this.screenH / 2 - y * cam.targetScale;
    cam.x = cam.targetX;
    cam.y = cam.targetY;
  }

  setZoom(z: number): void {
    const cam = this.camera;
    const cx = this.screenW / 2;
    const cy = this.screenH / 2;
    const newScale = zoomToScale(z, this.config);
    cam.zoomAnchorWX = (cx - cam.targetX) / cam.targetScale;
    cam.zoomAnchorWY = (cy - cam.targetY) / cam.targetScale;
    cam.zoomAnchorSX = cx;
    cam.zoomAnchorSY = cy;
    cam.isZooming = true;
    cam.targetScale = newScale;
    cam.targetX = cx - cam.zoomAnchorWX * newScale;
    cam.targetY = cy - cam.zoomAnchorWY * newScale;
  }

  fitBounds(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    opts: { padding?: Padding } = {},
  ): void {
    const cam = this.camera;
    const pad = this.effectivePadding(opts.padding);

    // When no per-call and no global padding, use the legacy 40px default
    const hasExplicitPadding = opts.padding != null ||
      (this.viewportPadding !== ZERO_PADDING);
    const effectivePad = hasExplicitPadding
      ? pad
      : { top: 40, right: 40, bottom: 40, left: 40 };

    const availW = this.screenW - effectivePad.left - effectivePad.right;
    const availH = this.screenH - effectivePad.top - effectivePad.bottom;
    const bw = Math.abs(x2 - x1);
    const bh = Math.abs(y2 - y1);
    const sx = availW / bw;
    const sy = availH / bh;
    const newScale = Math.min(sx, sy);
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;
    const { cx, cy } = this.paddedCenter(effectivePad);
    cam.targetScale = Math.max(
      cam.minScale,
      Math.min(cam.maxScale, newScale),
    );
    cam.targetX = cx - centerX * cam.targetScale;
    cam.targetY = cy - centerY * cam.targetScale;
  }

  getView(): ViewState {
    const cam = this.camera;
    const worldLeft = -cam.x / cam.scale;
    const worldTop = -cam.y / cam.scale;
    const worldRight = worldLeft + this.screenW / cam.scale;
    const worldBot = worldTop + this.screenH / cam.scale;
    return {
      x: (worldLeft + worldRight) / 2,
      y: (worldTop + worldBot) / 2,
      zoom: scaleToZoom(cam.scale, this.config),
      bounds: {
        x1: worldLeft,
        y1: worldTop,
        x2: worldRight,
        y2: worldBot,
      },
    };
  }

  // ── Layers ─────────────────────────────────────────────────────────────

  createLayer(
    id: string,
    opts: {
      visible?: boolean;
      opacity?: number;
      order?: number;
      label?: string;
      category?: string;
      hidden?: boolean;
      minZoom?: LayerMinZoom;
    } = {},
  ): void {
    if (this.layers.has(id)) return;

    const container = new PIXI.Container();
    container.visible = opts.visible !== false;
    container.alpha = opts.opacity ?? 1;

    const order = opts.order ?? this.nextOrder++;

    this.world.addChild(container);

    this.layers.set(id, {
      container,
      visible: container.visible,
      opacity: container.alpha,
      builtin: false,
      order,
      label: opts.label,
      category: opts.category,
      hidden: opts.hidden ?? false,
      minZoom: resolveMinZoom(opts.minZoom),
    });

    this.sortLayerContainers();
    this.rebuildLayerPanel();
  }

  createLayers(items: CreateLayersItem[]): void {
    for (const item of items) {
      if ("type" in item && item.type === "category") {
        for (const ld of item.layers) {
          this.createLayer(ld.id, {
            label: ld.label,
            visible: ld.visible,
            opacity: ld.opacity,
            hidden: ld.hidden,
            minZoom: ld.minZoom,
            category: item.label,
            order: this.nextOrder++,
          });
        }
      } else {
        const ld = item as LayerDef;
        this.createLayer(ld.id, {
          label: ld.label,
          visible: ld.visible,
          opacity: ld.opacity,
          hidden: ld.hidden,
          minZoom: ld.minZoom,
          order: this.nextOrder++,
        });
      }
    }
    // createLayer already calls sort + rebuild, but batch a final one
    this.sortLayerContainers();
    this.rebuildLayerPanel();
  }

  removeLayer(id: string): void {
    this.removeLayerInternal(id);
    this.rebuildLayerPanel();
  }

  clearLayers(): void {
    const toRemove: string[] = [];
    for (const [id, layer] of this.layers) {
      if (!layer.builtin) toRemove.push(id);
    }
    for (const id of toRemove) this.removeLayerInternal(id);
    this.rebuildLayerPanel();
  }

  private removeLayerInternal(id: string): void {
    const layer = this.layers.get(id);
    if (!layer || layer.builtin) return;

    // Remove all markers on this layer
    const markersToRemove: string[] = [];
    for (const [markerId, marker] of this.markers) {
      if (marker.data.layer === id) markersToRemove.push(markerId);
    }
    for (const mid of markersToRemove) this.markers.delete(mid);

    // Remove all overlays on this layer
    const overlaysToRemove: string[] = [];
    for (const [overlayId, overlay] of this.overlays) {
      if (overlay.data.layer === id) overlaysToRemove.push(overlayId);
    }
    for (const oid of overlaysToRemove) this.overlays.delete(oid);

    // Remove all heatmaps on this layer
    const heatmapsToRemove: string[] = [];
    for (const [heatmapId, heatmap] of this.heatmaps) {
      if (heatmap.data.layer === id) heatmapsToRemove.push(heatmapId);
    }
    for (const hid of heatmapsToRemove) {
      const hm = this.heatmaps.get(hid)!;
      hm.renderTexture.destroy(true);
      this.heatmaps.delete(hid);
    }

    // Destroy container and all children
    this.world.removeChild(layer.container);
    layer.container.destroy({ children: true });
    this.layers.delete(id);
    this.layerVisibilityCache.delete(id);

  }

  private sortLayerContainers(): void {
    const sorted = [...this.layers.entries()]
      .filter(([, l]) => !l.builtin)
      .sort((a, b) => a[1].order - b[1].order);
    // addChild on existing child moves it to the end, so adding in order sorts them
    for (const [, layer] of sorted) {
      this.world.addChild(layer.container);
    }
  }

  setLayerVisibility(id: string, visible: boolean): void {
    const layer = this.layers.get(id);
    if (!layer) return;
    layer.visible = visible;
    layer.container.visible = visible;
    this.rebuildLayerPanel();
  }

  setLayerOpacity(id: string, opacity: number): void {
    const layer = this.layers.get(id);
    if (!layer) return;
    layer.opacity = opacity;
    layer.container.alpha = opacity;
  }

  getLayers(): LayerInfo[] {
    const result: LayerInfo[] = [];
    for (const [id, layer] of this.layers) {
      result.push({
        id,
        visible: layer.visible,
        opacity: layer.opacity,
        order: layer.order,
        category: layer.category,
      });
    }
    return result;
  }

  // ── Layer panel ───────────────────────────────────────────────────────

  private rebuildLayerPanel(): void {
    const items: LayerPanelItem[] = [];
    for (const [id, entry] of this.layers) {
      if (entry.builtin || entry.hidden) continue;
      items.push({
        id,
        visible: entry.visible,
        label: entry.label,
        category: entry.category,
        order: entry.order,
      });
    }
    this.layerPanel.rebuild(items);
  }

  // ── Markers ────────────────────────────────────────────────────────────

  addMarker(opts: MarkerData): void {
    const {
      id,
      layer: layerId,
      x,
      y,
      icon,
      shape = "circle",
      text,
      tooltip,
      color = 0x50b0f0,
      size = 14,
    } = opts;

    const layer = this.layers.get(layerId);
    if (!layer) {
      console.warn(
        `[MapEngine] Layer "${layerId}" not found. Create it first with createLayer().`,
      );
      return;
    }

    // Remove existing marker with same ID
    this.removeMarker(id);

    const markerGroup = new PIXI.Container();
    markerGroup.x = x;
    markerGroup.y = y;

    // ── Glow ──────────────────────────────────────────────────────────
    const gfx = new PIXI.Graphics();
    gfx.beginFill(color, 0.12);
    gfx.drawCircle(0, 0, size * 2);
    gfx.endFill();

    // ── Shape fill + border ──────────────────────────────────────────
    gfx.lineStyle(1.5, 0xffffff, 0.3);
    gfx.beginFill(color, 0.9);
    drawShape(gfx, shape, size);
    gfx.endFill();

    markerGroup.addChild(gfx);

    // ── Interactive hit area ─────────────────────────────────────────
    gfx.eventMode = "static";
    gfx.cursor = "pointer";
    gfx.hitArea = new PIXI.Circle(0, 0, size * 1.5);
    gfx.on("pointerup", () => {
      if (this.input.wasDragMove) return;
      this.emitEvent("markerclick", { markerId: id, wx: x, wy: y, ...(opts.data ? { data: opts.data } : {}) });
    });

    // ── Icon ─────────────────────────────────────────────────────────
    let iconSprite: PIXI.Sprite | null = null;
    const hasIcon = icon != null;
    const hasText = text != null && text.length > 0;

    if (hasIcon) {
      const texture = getIconTexture(icon!);
      if (texture) {
        iconSprite = new PIXI.Sprite(texture);
        const iconSize = hasText ? size * 0.9 : size * 1.1;
        iconSprite.width = iconSize;
        iconSprite.height = iconSize;
        iconSprite.anchor.set(0.5, 0.5);
        if (hasText) {
          iconSprite.y = -size * 0.18;
        }
        markerGroup.addChild(iconSprite);
      }
    }

    // ── Text ─────────────────────────────────────────────────────────
    let textDisplay: PIXI.Text | null = null;
    if (hasText) {
      const truncated = text!.slice(0, 4);
      const fontSize = hasIcon ? size * 0.5 : size * 0.75;
      textDisplay = new PIXI.Text(truncated, {
        fontFamily: "monospace",
        fontSize,
        fill: 0xffffff,
        align: "center",
        fontWeight: "bold",
      });
      textDisplay.anchor.set(0.5, 0.5);
      if (hasIcon) {
        textDisplay.y = size * 0.5;
      }
      markerGroup.addChild(textDisplay);
    }

    // ── Tooltip ──────────────────────────────────────────────────────
    let tooltipContainer: PIXI.Container | null = null;
    const richTooltip = opts.richTooltip;
    const showTooltip = richTooltip || tooltip;

    if (showTooltip) {
      tooltipContainer = new PIXI.Container();
      tooltipContainer.visible = false;

      const isDark = this.currentTheme === "dark";
      const bgColor = isDark ? 0x1a1a2e : 0xffffff;
      const bgAlpha = isDark ? 0.92 : 0.95;
      const textColor = isDark ? 0xffffff : 0x1a1a2e;
      const subtitleColor = isDark ? 0xaaaaaa : 0x666666;
      const pad = 8;
      const caretSize = 5;

      const contentContainer = new PIXI.Container();

      if (richTooltip) {
        // ── Rich tooltip: title + optional subtitle + optional badge ──
        let yOffset = pad;

        const titleText = new PIXI.Text(richTooltip.title, {
          fontFamily: "sans-serif",
          fontSize: 12,
          fill: textColor,
          fontWeight: "bold",
        });
        titleText.x = pad;
        titleText.y = yOffset;
        contentContainer.addChild(titleText);
        yOffset += titleText.height + 2;

        let maxW = titleText.width;

        if (richTooltip.subtitle) {
          const subText = new PIXI.Text(richTooltip.subtitle, {
            fontFamily: "sans-serif",
            fontSize: 11,
            fill: subtitleColor,
          });
          subText.x = pad;
          subText.y = yOffset;
          contentContainer.addChild(subText);
          yOffset += subText.height + 2;
          maxW = Math.max(maxW, subText.width);
        }

        if (richTooltip.badge) {
          const badgePad = 4;
          const badgeText = new PIXI.Text(richTooltip.badge.text, {
            fontFamily: "sans-serif",
            fontSize: 10,
            fill: 0xffffff,
            fontWeight: "bold",
          });
          const badgeBg = new PIXI.Graphics();
          badgeBg.beginFill(richTooltip.badge.color, 1);
          badgeBg.drawRoundedRect(
            0, 0,
            badgeText.width + badgePad * 2,
            badgeText.height + badgePad * 2,
            3,
          );
          badgeBg.endFill();
          badgeBg.x = pad;
          badgeBg.y = yOffset;
          badgeText.x = pad + badgePad;
          badgeText.y = yOffset + badgePad;
          contentContainer.addChild(badgeBg);
          contentContainer.addChild(badgeText);
          yOffset += badgeText.height + badgePad * 2 + 2;
          maxW = Math.max(maxW, badgeText.width + badgePad * 2);
        }

        const totalW = maxW + pad * 2;
        const totalH = yOffset + pad - 2;

        // Background
        const bg = new PIXI.Graphics();
        bg.beginFill(bgColor, bgAlpha);
        bg.drawRoundedRect(0, 0, totalW, totalH, 6);
        bg.endFill();
        // Border
        bg.lineStyle(1, isDark ? 0x333355 : 0xcccccc, 0.5);
        bg.drawRoundedRect(0, 0, totalW, totalH, 6);

        tooltipContainer.addChild(bg);
        tooltipContainer.addChild(contentContainer);

        // Caret (triangle pointing down, below tooltip — default position is above marker)
        const caret = new PIXI.Graphics();
        caret.name = "caret";
        caret.beginFill(bgColor, bgAlpha);
        caret.moveTo(-caretSize, 0);
        caret.lineTo(caretSize, 0);
        caret.lineTo(0, caretSize);
        caret.closePath();
        caret.endFill();
        caret.x = totalW / 2;
        caret.y = totalH;
        tooltipContainer.addChild(caret);

        // Position tooltip above marker by default
        tooltipContainer.x = -totalW / 2;
        tooltipContainer.y = -(size + 6 + totalH + caretSize);

        // Store dimensions for smart repositioning
        (tooltipContainer as PIXI.Container & { _ttW: number; _ttH: number; _caretSize: number })._ttW = totalW;
        (tooltipContainer as PIXI.Container & { _ttH: number })._ttH = totalH;
        (tooltipContainer as PIXI.Container & { _caretSize: number })._caretSize = caretSize;
      } else {
        // ── Plain tooltip (backwards compatible) ──
        const tooltipText = new PIXI.Text(tooltip!, {
          fontFamily: "sans-serif",
          fontSize: 11,
          fill: textColor,
          align: "center",
        });
        tooltipText.anchor.set(0.5, 0);

        const bg = new PIXI.Graphics();
        bg.beginFill(bgColor, bgAlpha);
        bg.drawRoundedRect(
          -tooltipText.width / 2 - pad,
          0,
          tooltipText.width + pad * 2,
          tooltipText.height + pad * 2,
          4,
        );
        bg.endFill();

        tooltipContainer.addChild(bg);
        tooltipText.y = pad;
        tooltipContainer.addChild(tooltipText);
        tooltipContainer.y = size + 6;
      }

      markerGroup.addChild(tooltipContainer);

      // Save the original local offset so we can restore it when reparenting back
      const localTtX = tooltipContainer.x;
      const localTtY = tooltipContainer.y;

      const showTooltip = () => {
        if (!tooltipContainer) return;
        const markerScreenX = x * this.camera.scale + this.camera.x;
        const markerScreenY = y * this.camera.scale + this.camera.y;

        // Reparent into the stage-level overlay so tooltips render above all layers
        this.tooltipOverlay.addChild(tooltipContainer);
        tooltipContainer.visible = true;

        // Smart repositioning for rich tooltips: flip below if near top edge
        if (richTooltip) {
          const ttMeta = tooltipContainer as PIXI.Container & { _ttW: number; _ttH: number; _caretSize: number };
          const spaceAbove = markerScreenY - (size + 6 + ttMeta._ttH + ttMeta._caretSize);
          const caret = tooltipContainer.getChildByName("caret") as PIXI.Graphics;

          if (spaceAbove < 0) {
            // Flip: show below marker
            tooltipContainer.x = markerScreenX + localTtX;
            tooltipContainer.y = markerScreenY + size + 6 + ttMeta._caretSize;
            if (caret) {
              caret.clear();
              caret.beginFill(
                this.currentTheme === "dark" ? 0x1a1a2e : 0xffffff,
                this.currentTheme === "dark" ? 0.92 : 0.95,
              );
              caret.moveTo(-ttMeta._caretSize, ttMeta._caretSize);
              caret.lineTo(ttMeta._caretSize, ttMeta._caretSize);
              caret.lineTo(0, 0);
              caret.closePath();
              caret.endFill();
              caret.y = -ttMeta._caretSize;
            }
          } else {
            // Default: show above marker
            tooltipContainer.x = markerScreenX + localTtX;
            tooltipContainer.y = markerScreenY - (size + 6 + ttMeta._ttH + ttMeta._caretSize);
            if (caret) {
              caret.clear();
              caret.beginFill(
                this.currentTheme === "dark" ? 0x1a1a2e : 0xffffff,
                this.currentTheme === "dark" ? 0.92 : 0.95,
              );
              caret.moveTo(-ttMeta._caretSize, 0);
              caret.lineTo(ttMeta._caretSize, 0);
              caret.lineTo(0, ttMeta._caretSize);
              caret.closePath();
              caret.endFill();
              caret.y = ttMeta._ttH;
            }
          }

          // Horizontal clamping: prevent overflow left/right
          const tooltipLeft = tooltipContainer.x;
          const tooltipRight = tooltipLeft + ttMeta._ttW;
          if (tooltipLeft < 0) {
            tooltipContainer.x = 0;
          } else if (tooltipRight > this.screenW) {
            tooltipContainer.x = this.screenW - ttMeta._ttW;
          }
        } else {
          // Plain tooltip: position in screen space
          tooltipContainer.x = markerScreenX + localTtX;
          tooltipContainer.y = markerScreenY + localTtY;
        }
      };

      const hideTooltip = () => {
        if (!tooltipContainer) return;
        tooltipContainer.visible = false;
        // Reparent back to markerGroup and restore local coords
        markerGroup.addChild(tooltipContainer);
        tooltipContainer.x = localTtX;
        tooltipContainer.y = localTtY;
      };

      gfx.on("pointerover", showTooltip);
      gfx.on("pointerout", hideTooltip);

      // Dismiss tooltip on click (rich tooltips typically open a sidebar/navigate)
      if (richTooltip) {
        gfx.on("pointerdown", hideTooltip);
      }
    }

    // Apply counter-scale immediately so the marker isn't oversized
    // until the next zoom change triggers updateMarkers()
    if (this.camera.scale > 0) {
      markerGroup.scale.set(1 / this.camera.scale);
    }

    layer.container.addChild(markerGroup);
    this.markers.set(id, {
      container: markerGroup,
      gfx,
      iconSprite,
      textDisplay,
      tooltipContainer,
      tooltipLocalX: tooltipContainer?.x ?? 0,
      tooltipLocalY: tooltipContainer?.y ?? 0,
      data: opts,
      pulseScale: 1,
      cancelPulse: null,
    });

    // Invalidate the marker-update cache so the next animation frame
    // processes visibility / culling for the newly added marker.
    this.lastMarkerScale = -1;
  }

  updateMarker(id: string, opts: Partial<MarkerData>): void {
    const marker = this.markers.get(id);
    if (!marker) return;

    // Merge and re-create
    const newData: MarkerData = { ...marker.data, ...opts };
    this.removeMarker(id);
    this.addMarker(newData);
  }

  removeMarker(id: string): void {
    const marker = this.markers.get(id);
    if (!marker) return;
    marker.cancelPulse?.();
    // If the tooltip is currently reparented into the overlay, clean it up
    if (marker.tooltipContainer?.parent === this.tooltipOverlay) {
      this.tooltipOverlay.removeChild(marker.tooltipContainer);
      marker.tooltipContainer.destroy({ children: true });
    }
    marker.container.parent?.removeChild(marker.container);
    marker.container.destroy({ children: true });
    this.markers.delete(id);
  }

  clearMarkers(layer?: string): void {
    const toRemove: string[] = [];
    for (const [id, marker] of this.markers) {
      if (!layer || marker.data.layer === layer) {
        toRemove.push(id);
      }
    }
    for (const id of toRemove) {
      this.removeMarker(id);
    }
  }

  // ── Marker per-frame update ────────────────────────────────────────

  private updateMarkers(): void {
    const worldScale = this.camera.scale;
    const camX = this.camera.x;
    const camY = this.camera.y;

    // Check if visibility changed for any layer.
    // Floor at 1 so layers with minZoom: 1 stay visible even when the
    // camera zooms out past zoom 1 on small/wide viewports.
    const currentZoom = Math.max(1, scaleToZoom(worldScale, this.config));
    let visibilityChanged = false;
    for (const [id, entry] of this.layers) {
      const vis = entry.visible && currentZoom >= entry.minZoom;
      if (this.layerVisibilityCache.get(id) !== vis) {
        visibilityChanged = true;
        this.layerVisibilityCache.set(id, vis);
      }
    }

    const scaleChanged = worldScale !== this.lastMarkerScale;
    const cameraMoved = camX !== this.lastMarkerCamX || camY !== this.lastMarkerCamY;

    // Nothing to do — skip the entire marker loop
    if (!visibilityChanged && !scaleChanged && !cameraMoved && this.activePulses === 0) return;

    this.lastMarkerScale = worldScale;
    this.lastMarkerCamX = camX;
    this.lastMarkerCamY = camY;

    const counterScale = 1 / worldScale;

    // Viewport bounds in world space, with padding for markers near edges
    const pad = 50 * counterScale;
    const left = -camX / worldScale - pad;
    const top = -camY / worldScale - pad;
    const right = left + this.screenW / worldScale + pad * 2;
    const bottom = top + this.screenH / worldScale + pad * 2;

    for (const [, marker] of this.markers) {
      const layerVisible =
        this.layerVisibilityCache.get(marker.data.layer) !== false;

      // Cull markers outside the viewport
      const mx = marker.data.x;
      const my = marker.data.y;
      const inView = mx >= left && mx <= right && my >= top && my <= bottom;

      const wasVisible = marker.container.visible;
      const nowVisible = layerVisible && inView;
      marker.container.visible = nowVisible;

      // Counter-scale: keep marker at fixed screen-pixel size.
      // Apply when scale changed OR when a culled marker enters the viewport
      // with a stale scale, OR when a pulse animation is active.
      if (nowVisible && (scaleChanged || !wasVisible || marker.pulseScale !== 1)) {
        marker.container.scale.set(counterScale * marker.pulseScale);
      }
    }
  }

  // ── Overlays ───────────────────────────────────────────────────────

  addOverlay(opts: OverlayData): void {
    const {
      id,
      layer: layerId,
      _type,
      points,
      color = 0xf0c050,
      width = 2,
      alpha = 0.8,
      fill,
    } = opts;

    const layer = this.layers.get(layerId);
    if (!layer) {
      console.warn(
        `[MapEngine] Layer "${layerId}" not found. Create it first with createLayer().`,
      );
      return;
    }

    this.removeOverlay(id);

    const gfx = new PIXI.Graphics();

    if (_type === "polyline" && points.length > 1) {
      gfx.lineStyle(width, color, alpha);
      gfx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        gfx.lineTo(points[i].x, points[i].y);
      }
    } else if (_type === "polygon" && points.length > 2) {
      gfx.beginFill(fill ?? color, alpha * 0.3);
      gfx.lineStyle(width, color, alpha);
      gfx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        gfx.lineTo(points[i].x, points[i].y);
      }
      gfx.closePath();
      gfx.endFill();
    }

    layer.container.addChild(gfx);
    this.overlays.set(id, { gfx, data: opts });
  }

  removeOverlay(id: string): void {
    const overlay = this.overlays.get(id);
    if (!overlay) return;
    overlay.gfx.parent?.removeChild(overlay.gfx);
    overlay.gfx.destroy();
    this.overlays.delete(id);
  }

  clearOverlays(layer?: string): void {
    const toRemove: string[] = [];
    for (const [id, overlay] of this.overlays) {
      if (!layer || overlay.data.layer === layer) {
        toRemove.push(id);
      }
    }
    for (const id of toRemove) {
      this.removeOverlay(id);
    }
  }

  // ── Heatmaps ──────────────────────────────────────────────────────

  addHeatmap(opts: HeatmapData): void {
    const {
      id,
      layer: layerId,
      points,
      color = 0xff4400,
      radius = 80,
    } = opts;

    const layer = this.layers.get(layerId);
    if (!layer) {
      console.warn(
        `[MapEngine] Layer "${layerId}" not found. Create it first with createLayer().`,
      );
      return;
    }

    this.removeHeatmap(id);
    if (points.length === 0) return;

    // Normalize intensities to 0–1 relative to the dataset
    let minI = Infinity;
    let maxI = -Infinity;
    for (const p of points) {
      if (p.intensity < minI) minI = p.intensity;
      if (p.intensity > maxI) maxI = p.intensity;
    }
    const rangeI = maxI - minI || 1;

    // Bounding box with padding for the largest possible point radius
    const maxPointRadius = radius * 2;
    let bx1 = Infinity;
    let by1 = Infinity;
    let bx2 = -Infinity;
    let by2 = -Infinity;
    for (const p of points) {
      bx1 = Math.min(bx1, p.x - maxPointRadius);
      by1 = Math.min(by1, p.y - maxPointRadius);
      bx2 = Math.max(bx2, p.x + maxPointRadius);
      by2 = Math.max(by2, p.y + maxPointRadius);
    }

    const boundsW = bx2 - bx1;
    const boundsH = by2 - by1;

    // Auto-scale resolution so the texture stays within 2048px per side
    const maxTexDim = 2048;
    const pxPerUnit = Math.min(1, maxTexDim / Math.max(boundsW, boundsH));
    const texW = Math.max(1, Math.ceil(boundsW * pxPerUnit));
    const texH = Math.max(1, Math.ceil(boundsH * pxPerUnit));

    // Render all gradient sprites into a RenderTexture with additive blending.
    // This lets points accumulate into hotspots without affecting underlying layers.
    const renderTexture = PIXI.RenderTexture.create({
      width: texW,
      height: texH,
    });

    const tempContainer = new PIXI.Container();
    const gradTex = getGradientTexture();

    for (const p of points) {
      const t = (p.intensity - minI) / rangeI; // 0 → 1

      // Higher intensity → bigger radius, lower per-point alpha.
      // Overlap accumulates via additive blending, so dense areas still glow.
      const pointRadius = radius * (0.3 + t * 1.7);
      const pointAlpha = 0.08 + (1 - t) * 0.22;

      const s = new PIXI.Sprite(gradTex);
      s.anchor.set(0.5);
      s.x = (p.x - bx1) * pxPerUnit;
      s.y = (p.y - by1) * pxPerUnit;
      s.width = pointRadius * 2 * pxPerUnit;
      s.height = pointRadius * 2 * pxPerUnit;
      s.alpha = pointAlpha;
      s.tint = color;
      s.blendMode = PIXI.BLEND_MODES.ADD;

      tempContainer.addChild(s);
    }

    this.app.renderer.render(tempContainer, { renderTexture, clear: true });
    tempContainer.destroy({ children: true });

    // Place the baked texture in world space with normal blending
    const worldSprite = new PIXI.Sprite(renderTexture);
    worldSprite.x = bx1;
    worldSprite.y = by1;
    worldSprite.width = boundsW;
    worldSprite.height = boundsH;

    layer.container.addChild(worldSprite);
    this.heatmaps.set(id, { sprite: worldSprite, renderTexture, data: opts });
  }

  removeHeatmap(id: string): void {
    const entry = this.heatmaps.get(id);
    if (!entry) return;
    entry.sprite.parent?.removeChild(entry.sprite);
    entry.sprite.destroy();
    entry.renderTexture.destroy(true);
    this.heatmaps.delete(id);
  }

  clearHeatmaps(layer?: string): void {
    const toRemove: string[] = [];
    for (const [id, entry] of this.heatmaps) {
      if (!layer || entry.data.layer === layer) {
        toRemove.push(id);
      }
    }
    for (const id of toRemove) {
      this.removeHeatmap(id);
    }
  }

  clearLayer(id: string): void {
    this.clearMarkers(id);
    this.clearOverlays(id);
    this.clearHeatmaps(id);
  }

  // ── Marker lookup & highlight ──────────────────────────────────────

  /** Get the data for a marker by ID, or null if not found. */
  getMarker(id: string): MarkerData | null {
    return this.markers.get(id)?.data ?? null;
  }

  /** Fly to a marker and briefly pulse its glow to draw attention. */
  highlightMarker(id: string, opts?: HighlightMarkerOptions): void {
    const entry = this.markers.get(id);
    if (!entry) {
      console.warn(`[MapEngine] Marker "${id}" not found for highlight`);
      return;
    }

    const { x, y } = entry.data;

    // Determine zoom: use the explicit value, or stay at current zoom if
    // it's high enough for the marker's layer to be visible. Otherwise
    // zoom in to the layer's minZoom so the marker is actually shown.
    let zoom = opts?.zoom;
    if (zoom == null) {
      const layer = this.layers.get(entry.data.layer);
      const layerMinZoom = layer?.minZoom ?? 0;
      const currentZoom = scaleToZoom(this.camera.targetScale, this.config);
      if (currentZoom < layerMinZoom) {
        zoom = layerMinZoom;
      }
    }
    this.flyTo(x, y, { zoom, padding: opts?.padding });

    // Cancel any existing pulse on this marker before starting a new one
    entry.cancelPulse?.();

    // Pulse the glow via a scale multiplier that updateMarkers applies
    // on top of the counter-scale. This avoids fighting with counter-scale
    // updates during flyTo zoom animations.
    const PULSE_PEAK = 1.8;
    const PULSE_FRAMES = 30; // ≈ 0.5s at 60fps
    entry.pulseScale = PULSE_PEAK;
    this.activePulses++;

    let pulseCount = 0;
    const pulseTicker = () => {
      pulseCount++;
      if (pulseCount >= PULSE_FRAMES) {
        entry.pulseScale = 1;
        this.activePulses--;
        entry.cancelPulse = null;
        this.app.ticker.remove(pulseTicker);
      } else {
        const t = pulseCount / PULSE_FRAMES;
        const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic
        entry.pulseScale = PULSE_PEAK + (1 - PULSE_PEAK) * ease;
      }
    };
    this.app.ticker.add(pulseTicker);

    entry.cancelPulse = () => {
      entry.pulseScale = 1;
      this.activePulses--;
      entry.cancelPulse = null;
      this.app.ticker.remove(pulseTicker);
    };
  }

  // ── Display options ────────────────────────────────────────────────

  setInteractive(enabled: boolean): void {
    this.input.setEnabled(enabled);
  }

  showMinimap(visible: boolean): void {
    this.minimap.show(visible);
  }

  showControls(visible: boolean): void {
    this.controls.show(visible);
  }

  setTheme(theme: string): void {
    this.currentTheme = theme === "light" ? "light" : "dark";
    if (theme === "light") {
      this.app.renderer.background.color = 0xf0f0f0;
    } else {
      this.app.renderer.background.color = this.config.backgroundColor;
    }
  }


  // ── Image overlays ─────────────────────────────────────────────────

  /**
   * Add an image overlay to a layer. The image is loaded asynchronously
   * and sized to cover the entire world (worldSize × worldSize).
   *
   * For a grid of images, pass imageGrid with rows/cols and use {row}/{col}
   * placeholders in the URL. Each cell covers worldSize/cols × worldSize/rows.
   * Empty cells (404s) are silently skipped.
   */
  addImageOverlay(
    layerId: string,
    imageUrl: string,
    opts: { opacity?: number; imageGrid?: { rows: number; cols: number } } = {},
  ): void {
    const layer = this.layers.get(layerId);
    if (!layer) {
      console.warn(
        `[MapEngine] Layer "${layerId}" not found. Create it first with createLayer().`,
      );
      return;
    }

    if (opts.imageGrid) {
      const { rows, cols } = opts.imageGrid;
      const cellW = this.config.worldSize / cols;
      const cellH = this.config.worldSize / rows;

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const url = imageUrl
            .replace("{row}", String(row))
            .replace("{col}", String(col));
          this._loadImageSprite(layer, url, col * cellW, row * cellH, cellW, cellH, opts.opacity);
        }
      }
    } else {
      this._loadImageSprite(layer, imageUrl, 0, 0, this.config.worldSize, this.config.worldSize, opts.opacity);
    }
  }

  private _loadImageSprite(
    layer: LayerEntry,
    url: string,
    x: number,
    y: number,
    width: number,
    height: number,
    opacity?: number,
  ): void {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const texture = PIXI.Texture.from(img);
      texture.baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
      const sprite = new PIXI.Sprite(texture);
      sprite.x = x;
      sprite.y = y;
      sprite.width = width;
      sprite.height = height;
      if (opacity !== undefined) {
        sprite.alpha = opacity;
      }
      layer.container.addChild(sprite);
    };
    img.onerror = () => {
      console.warn(`[MapEngine] Failed to load image overlay: ${url}`);
    };
    img.src = url;
  }

  // ── Cleanup ────────────────────────────────────────────────────────

  /** Hide every visible tooltip and reparent back to its marker group. */
  private dismissAllTooltips(): void {
    for (const [, marker] of this.markers) {
      const tc = marker.tooltipContainer;
      if (!tc || !tc.visible) continue;
      tc.visible = false;
      // If it's currently in the overlay, move it back to its marker group
      if (tc.parent === this.tooltipOverlay) {
        marker.container.addChild(tc);
        tc.x = marker.tooltipLocalX;
        tc.y = marker.tooltipLocalY;
      }
    }
  }

  destroy(): void {
    window.removeEventListener("blur", this._onBlur);
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    this.input.destroy();
    this.tileManager.destroy();
    this.layerPanel.destroy();
    this.minimap.destroy();
    this.controls.destroy();
    this.app.destroy(true);
  }
}
